import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useT } from '../services/i18n';
import $ from 'jquery';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

let ready;
const script = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[data-mr-src="${src}"]`)) return resolve();
  const s = document.createElement('script'); s.src = src; s.async = true; s.dataset.mrSrc = src; s.onload = resolve; s.onerror = () => reject(new Error(`Chargement impossible : ${src}`)); document.head.appendChild(s);
});
// jQuery and Leaflet are bundled (no third-party CDN, stricter CSP); only the official Mondial Relay plugin is loaded remotely
const loadWidget = () => {
  if (!ready) {
    window.jQuery = window.$ = $;
    window.L = L;
    // The MR plugin re-injects Leaflet from unpkg unless a <script>/<link> with that exact URL already exists: inert placeholders (never fetched) make it skip
    if (!document.querySelector('[data-mr-shim]')) {
      const sh = document.createElement('script'); sh.type = 'text/plain'; sh.src = 'https://unpkg.com/leaflet/dist/leaflet.js'; sh.dataset.mrShim = '1'; document.head.appendChild(sh);
      const lk = document.createElement('link'); lk.rel = 'alternate'; lk.href = 'https://unpkg.com/leaflet/dist/leaflet.css'; lk.dataset.mrShim = '1'; document.head.appendChild(lk);
    }
    ready = script('https://widget.mondialrelay.com/parcelshop-picker/jquery.plugin.mondialrelay.parcelshoppicker.min.js');
  }
  return ready;
};
const COUNTRY_CODES = { France: 'FR', Belgique: 'BE', Allemagne: 'DE', Italie: 'IT', Espagne: 'ES', 'Pays-Bas': 'NL', Luxembourg: 'LU', Portugal: 'PT', Autriche: 'AT' };

// Official Mondial Relay parcel shop picker (Leaflet map) in a dialog; returns the normalized selected point
const MondialRelayPicker = ({ open, onOpenChange, postalCode, country, brand, onSelect }) => {
  const { t } = useT();
  const host = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError(''); setLoading(true); setPicked(null);
    loadWidget().then(() => {
      if (cancelled || !host.current) return;
      const $ = window.jQuery;
      host.current.replaceChildren();
      // The widget injects an inline <script> (labels) and inline onclick= handlers, both blocked by our CSP: predefine the labels and bind CSP-safe delegated handlers
      window.MondialRelayLanguage = window.MondialRelayLanguage || { Horaires: 'Horaires', Photo: 'Photo' };
      $(host.current).off('.mrcsp')
        .on('click.mrcsp', '.PR-List-Item', function () { $(host.current).trigger('FocusOnMap', $(host.current).find('.PR-List-Item').index(this)); })
        .on('click.mrcsp', '.Tabs-Btn', function () { $(host.current).trigger('TabSelect', String(this.id || '').replace(/^btn_/, '')); });
      $(host.current).MR_ParcelShopPicker({
        Target: '#mr-selected-hidden', Brand: (brand || 'BDTEST').padEnd(8, ' '), Country: COUNTRY_CODES[country] || 'FR', PostCode: postalCode || '',
        Responsive: true, ShowResultsOnMap: true, Theme: 'mondialrelay', CSS: 1,
        OnNoResultReturned: () => setError('Aucun point relais trouvé pour ce code postal.'),
        OnParcelShopSelected: (d) => setPicked({ id: String(d.ID), name: String(d.Nom || ''), address1: String(d.Adresse1 || ''), address2: String(d.Adresse2 || ''), postal_code: String(d.CP || ''), city: String(d.Ville || ''), country: String(d.Pays || ''), lat: d.Lat ? String(d.Lat) : null, lon: d.Long ? String(d.Long) : null }),
      });
      setLoading(false);
      setTimeout(() => { try { $(host.current).trigger('MR_RebindMap'); } catch { /* noop */ } }, 400);
    }).catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [open, postalCode, country, brand]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="relay-picker-dialog">
        <DialogTitle className="flex items-center gap-2"><MapPin className="text-orange-500" size={18} /> {t('checkout.relayTitle')} — Mondial Relay</DialogTitle>
        {loading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Chargement de la carte…</div>}
        <input id="mr-selected-hidden" type="hidden" />
        <div ref={host} className="min-h-[460px]" data-testid="relay-picker-widget" />
        {error && <p className="text-sm text-red-600" data-testid="relay-picker-error">{error}</p>}
        <div className="flex items-center justify-between gap-3 pt-2 border-t">
          <p className="text-sm text-gray-700" data-testid="relay-picker-selected">{picked ? <><strong>{picked.name}</strong> — {picked.address1}, {picked.postal_code} {picked.city}</> : 'Cliquez sur un point relais dans la liste ou sur la carte.'}</p>
          <Button disabled={!picked} onClick={() => { onSelect(picked); onOpenChange(false); }} className="bg-orange-500 hover:bg-orange-600 shrink-0" data-testid="relay-picker-confirm">Valider ce point relais</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MondialRelayPicker;
